import { supabase } from '@/lib/supabase';

/**
 * The CRM account of an app object (F5f, L48), found by its CRM ID (authoritative,
 * e.g. after « Ajouter au CRM »), else by the exact domain of its keys (website or
 * e-mail domain), never by name. `rowId`/`runId` let the page offer « Ajouter au
 * CRM » on the run's row when the company is not there.
 */
export type AppRecordAccount =
  | { status: 'found'; by: 'crm_id' | 'domain'; domain: string | null; subjectId: string; name: string | null }
  | { status: 'not_found' | 'no_domain'; domain: string | null; runId: string | null; rowId: string | null; rowLabel: string | null }
  | { status: 'ambiguous'; domain: string; count: number }
  | { status: 'no_crm'; domain: string };

const text = (value: unknown): string | null => typeof value === 'string' && value.trim() ? value.trim() : null;

export const toAppRecordAccount = (value: unknown): AppRecordAccount | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const data = value as Record<string, unknown>;
  const domain = text(data.domain);
  if (data.status === 'found' && text(data.subjectId) && (domain || data.by === 'crm_id')) {
    return { status: 'found', by: data.by === 'crm_id' ? 'crm_id' : 'domain', domain, subjectId: text(data.subjectId)!, name: text(data.name) };
  }
  if (data.status === 'not_found' || data.status === 'no_domain') {
    return { status: data.status, domain, runId: text(data.runId), rowId: text(data.rowId), rowLabel: text(data.rowLabel) };
  }
  if (data.status === 'ambiguous' && domain) return { status: 'ambiguous', domain, count: typeof data.count === 'number' ? data.count : 2 };
  if (data.status === 'no_crm' && domain) return { status: 'no_crm', domain };
  return null;
};

export const readAppRecordAccount = async (recordId: string): Promise<AppRecordAccount | null> => {
  const { data, error } = await supabase.rpc('read_app_record_account_f5f', { p_record_id: recordId });
  if (error) throw new Error('Le compte lié n’a pas pu être chargé.');
  return toAppRecordAccount(data);
};
